import isEmpty from 'lodash/isEmpty';
import compact from 'lodash/compact';
import { buildHistogram, measurementValue, type HistogramSummary } from '~/utils/histogram';
import type { IDistributionWidgetModel } from './model';

// Readings the widget bins at most, as the report does: longer periods need an aggregation
export const MAX_READINGS = 150_000;

export default (self: IDistributionWidgetModel) => {
  // The parameter's data point of each device, in the order the devices were selected
  function deviceDataPoints() {
    const dataPointIds = self.parameter?.data_point_ids || [];
    return compact(
      self.node_id.map((nodeId) => {
        const node = self.nodes?.getById(nodeId);
        const dataPoint = dataPointIds.map((id) => node?.data_points?.getById(id)).find(Boolean);
        return node && dataPoint ? { node, dataPoint } : null;
      })
    );
  }

  return {
    // The parameter's data point on the first device, which names the parameter
    get parameterDataPoint() {
      return deviceDataPoints()[0]?.dataPoint ?? null;
    },
    get devices() {
      return deviceDataPoints().map(({ node, dataPoint }) => ({
        id: node._id as string,
        name: node.presentName as string,
        serial: node.serial as string,
        precision: dataPoint.precision as number,
        readings: compact(
          dataPoint._measurements.map((measurement) => {
            const value = measurementValue(measurement);
            return value === null ? null : ([measurement[0], value] as [number, number]);
          })
        )
      }));
    },
    get readingsCount() {
      return self.devices.reduce((sum, { readings }) => sum + readings.length, 0);
    },
    get isTooManyReadings() {
      return self.readingsCount > MAX_READINGS;
    },
    get summary(): HistogramSummary | null {
      if (!self.parameter || self.devices.length === 0 || self.isTooManyReadings) return null;
      const now = Date.now();
      return buildHistogram(self.devices, self.parameter.levels || [], {
        from: self.period?.from ?? now,
        to: self.period?.to ?? now,
        aggregate: self.aggregate
      });
    },
    // Live readings are raw values, which would mix with the averages of an aggregation
    get acceptsLiveUpdates() {
      return !self.aggregate;
    },
    get title() {
      return isEmpty(self.name) ? self.devices.map(({ name }) => name).join(' | ') : self.name;
    }
  };
};
